import { MEMBER_COLORS, MEMBER_LABELS } from './memberStates.js';
import L from 'leaflet';
import { getSessionTypeColor, routePassageColor, PASSAGE_COLORS } from './timelineHelper.js';
import { arrowsAlong } from './routeArrows.js';
import { isSameTrail } from './sessionHelper.js';

/**
 * Lit les coordonnées et les formate pour Leaflet
 */
/** Flèches de sens le long d'un tracé (couche remplacée à chaque changement). */
const drawArrows = (map, layers, key, latLngs, color) => {
    if (layers[key]) { map.removeLayer(layers[key]); layers[key] = null; }
    if (!latLngs || latLngs.length < 2) return;
    const markers = arrowsAlong(latLngs).map(a => L.marker([a.lat, a.lng], {
        interactive: false,
        keyboard: false,
        icon: L.divIcon({
            className: 'route-arrow-icon',
            html: `<div style="transform: rotate(${a.bearing}deg); color: ${color}; font-size: 16px; font-weight: 900; line-height: 16px; text-shadow: 0 0 2px #000, 0 0 2px #000;">▲</div>`,
            iconSize: [16, 16],
            iconAnchor: [8, 8],
        }),
    }));
    layers[key] = L.layerGroup(markers).addTo(map);
};

/** Couleur d'un membre de session partagée selon son état. */
// Mêmes couleurs et libellés que l'app (memberStates.js).
const RESPONDER_COLORS = MEMBER_COLORS;
const RESPONDER_LABELS = MEMBER_LABELS;

const formatPoints = (pts) => {
    if (!pts || !Array.isArray(pts)) return [];
    const f = pts.map(p => [Number(p.latitude ?? p.lat), Number(p.longitude ?? p.lng)]);
    return f.filter(p => Number.isFinite(p[0]) && Number.isFinite(p[1]));
};

const pointsHash = points => points.map(point => `${point[0]},${point[1]}`).join('|');

/**
 * Configure les calques spécifiques pour forcer l'ordre Z-Index des éléments tracés (du fond vers le haut)
 */
const ensurePanes = (map) => {
    if (!map) return;
    if (!map.getPane('corridorPane')) {
        map.createPane('corridorPane').style.zIndex = 401; // 1. Corridor (en bas)
        map.createPane('routePane').style.zIndex = 402;   // 2. Itinéraire de la victime (théorique)
        map.createPane('historyPane').style.zIndex = 403; // 3. Tracé historique de la victime
        map.createPane('responderRoutePane').style.zIndex = 404; // 4. Tracés itinéaires des responders (en haut des lignes)
    }
};

/**
 * Met à jour le tracé historique (Ligne continue avec segments colorés) et les marqueurs de façon incrémentale.
 * Utilise `polyline.setLatLngs()` pour une performance maximale sans destruction/re-création de couches DOM.
 */
export function updatePath(positions, map, layers) {
    if (!map) return;
    ensurePanes(map);

    if (!positions || positions.length < 1) {
        if (layers.pathLine) { map.removeLayer(layers.pathLine); layers.pathLine = null; }
        if (layers.pathSegments) {
            layers.pathSegments.forEach(item => { if (item.layer) map.removeLayer(item.layer); });
            layers.pathSegments = [];
        }
        if (layers.pathMarkers) {
            layers.pathMarkers.forEach(m => map.removeLayer(m));
            layers.pathMarkers = [];
        }
        return;
    }

    if (!layers.pathSegments) layers.pathSegments = [];
    if (!layers.pathMarkers) layers.pathMarkers = [];

    // 1. Découpage optimisé en segments de couleur
    const targetSegments = [];
    let currentPoints = [];
    let currentType = null;

    for (let i = 0; i < positions.length; i++) {
        const p = positions[i];
        const lat = p.latitude !== undefined ? Number(p.latitude) : Number(p.lat);
        const lng = p.longitude !== undefined ? Number(p.longitude) : Number(p.lng);
        const cType = p.sessionType || 'NORMAL';

        if (isNaN(lat) || isNaN(lng)) continue;

        if (currentType === null) {
            currentType = cType;
            currentPoints.push([lat, lng]);
        } else if (cType === currentType) {
            currentPoints.push([lat, lng]);
        } else {
            // Changement de type : on ajoute le point de raccord au segment précédent
            currentPoints.push([lat, lng]);
            if (currentPoints.length > 1) {
                targetSegments.push({
                    color: getSessionTypeColor(currentType),
                    points: currentPoints
                });
            }
            // Et on initie le nouveau segment avec ce même point
            currentType = cType;
            currentPoints = [[lat, lng]];
        }
    }

    if (currentPoints.length > 1) {
        targetSegments.push({
            color: getSessionTypeColor(currentType),
            points: currentPoints
        });
    }

    // 2. Mise à jour IN-PLACE des Polylines Leaflet (setLatLngs)
    const newPathSegments = [];

    for (let i = 0; i < targetSegments.length; i++) {
        const target = targetSegments[i];
        const existing = layers.pathSegments[i];

        if (existing && existing.color === target.color && existing.layer) {
            // Mise à jour fluide en mémoire
            existing.layer.setLatLngs(target.points);
            newPathSegments.push(existing);
        } else {
            if (existing && existing.layer) {
                map.removeLayer(existing.layer);
            }
            const poly = L.polyline(target.points, {
                color: target.color,
                weight: 6,
                opacity: 0.85,
                pane: 'historyPane'
            }).addTo(map);
            newPathSegments.push({ color: target.color, layer: poly });
        }
    }

    // Supprimer les segments excédentaires si on a reculé dans le temps
    for (let i = targetSegments.length; i < layers.pathSegments.length; i++) {
        if (layers.pathSegments[i]?.layer) {
            map.removeLayer(layers.pathSegments[i].layer);
        }
    }

    layers.pathSegments = newPathSegments;

    // 3. Mise à jour des KeyEvents (points d'alerte / changement de mode)
    const keyEvents = positions.filter(p => p.isKeyEvent === true || p.isKeyEvent === 'true');
    const keyEventsHash = keyEvents.map(k => `${k.timestamp || ''}_${k.lat ?? k.latitude}_${k.lng ?? k.longitude}`).join('|');

    if (layers._lastKeyEventsHash !== keyEventsHash) {
        layers._lastKeyEventsHash = keyEventsHash;
        layers.pathMarkers.forEach(m => map.removeLayer(m));
        layers.pathMarkers = [];

        keyEvents.forEach(p => {
            const lat = Number(p.latitude ?? p.lat);
            const lng = Number(p.longitude ?? p.lng);
            const timestamp = p.timestamp;
            const sessionType = p.sessionType || 'NORMAL';
            const color = getSessionTypeColor(sessionType);

            const icon = L.divIcon({
                className: 'custom-keyevent-icon',
                html: `<div style="background-color: ${color}; width: 18px; height: 18px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.4);"></div>`,
                iconSize: [24, 24],
                iconAnchor: [12, 12]
            });

            const m = L.marker([lat, lng], { icon, zIndexOffset: 50 }).addTo(map);
            if (timestamp) {
                const date = new Date(timestamp);
                const timeStr = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                m.bindPopup(`<b>🚨 Événement : ${sessionType}</b><br/>Heure : ${timeStr}`);
            }
            layers.pathMarkers.push(m);
        });
    }
}

/**
 * Dessine l'ensemble de la session (Itinéraire bleu, Corridor, Victime, Secoureurs)
 * avec mémoïsation pour éviter les re-rendus inutiles.
 */
export function renderVirtualSessionToMap(session, map, layers, responderMarkersRef) {
    if (!map || !session) return;
    ensurePanes(map);

    // 1. Itinéraire ou parcours (théorique) — mis à jour uniquement si les points ou le mode changent
    const routeColor = routePassageColor(session.travelMode);
    // Parcours rejoint, son tracé pas encore en place : l'itinéraire est retiré, le parcours reste.
    const joiningTrail = (session.trailApproachPoints || []).length >= 2 && routeColor === PASSAGE_COLORS.trail &&
        !isSameTrail(session.routePoints, session.trailApproachPoints);
    const routePoints = joiningTrail ? [] : formatPoints(session.routePoints);
    const routeHash = pointsHash(routePoints) + routeColor;

    if (routePoints.length > 0) {
        if (layers._lastRouteHash !== routeHash) {
            layers._lastRouteHash = routeHash;
            if (layers.routeLayer) map.removeLayer(layers.routeLayer);
            layers.routeLayer = L.polyline(routePoints, { color: routeColor, weight: 6, opacity: 0.8, dashArray: '10, 10', pane: 'routePane' }).addTo(map);
            // Sens de l'itinéraire, comme dans l'application.
            drawArrows(map, layers, 'routeArrows', routePoints, '#FFFFFF');

            // Marker Début d'itinéraire
            const startPt = routePoints[0];
            if (layers.startMarker) map.removeLayer(layers.startMarker);
            layers.startMarker = L.marker(startPt, {
                icon: L.divIcon({
                    className: 'start-route-icon',
                    html: `<div style="background-color: #4CAF50; width: 36px; height: 36px; border-radius: 50%; border: 3px solid white; display: flex; align-items: center; justify-content: center; font-size: 18px; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">🚩</div>`,
                    iconSize: [42, 42],
                    iconAnchor: [21, 21]
                }),
                zIndexOffset: 100
            }).addTo(map);

            // Marker Fin d'itinéraire
            const endPt = routePoints[routePoints.length - 1];
            if (layers.endMarker) map.removeLayer(layers.endMarker);
            layers.endMarker = L.marker(endPt, {
                icon: L.divIcon({
                    className: 'end-route-icon',
                    html: `<div style="background-color: #9C27B0; width: 36px; height: 36px; border-radius: 50%; border: 3px solid white; display: flex; align-items: center; justify-content: center; font-size: 18px; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">🏁</div>`,
                    iconSize: [42, 42],
                    iconAnchor: [21, 21]
                }),
                zIndexOffset: 100
            }).addTo(map);
        }
    } else {
        layers._lastRouteHash = '';
        if (layers.routeLayer) { map.removeLayer(layers.routeLayer); layers.routeLayer = null; }
        if (layers.routeArrows) { map.removeLayer(layers.routeArrows); layers.routeArrows = null; }
        if (layers.startMarker) { map.removeLayer(layers.startMarker); layers.startMarker = null; }
        if (layers.endMarker) { map.removeLayer(layers.endMarker); layers.endMarker = null; }
    }

    // 1 bis. Parcours visé pendant l'itinéraire qui y mène : trait plein vert et son sens
    // (plus visé si l'approche est abandonnée sans démarrer le parcours).
    const approachAbandoned = session.trailApproachLeaving && routeColor !== PASSAGE_COLORS.trail;
    const approachPoints = approachAbandoned ? [] : formatPoints(session.trailApproachPoints);
    const approachHash = pointsHash(approachPoints);
    if (approachPoints.length >= 2) {
        if (layers._lastApproachHash !== approachHash) {
            layers._lastApproachHash = approachHash;
            if (layers.approachTrailLayer) map.removeLayer(layers.approachTrailLayer);
            layers.approachTrailLayer = L.polyline(approachPoints, { color: PASSAGE_COLORS.trail, weight: 6, opacity: 0.85, pane: 'routePane' }).addTo(map);
            drawArrows(map, layers, 'approachTrailArrows', approachPoints, '#FFFFFF');
        }
    } else {
        layers._lastApproachHash = '';
        if (layers.approachTrailLayer) { map.removeLayer(layers.approachTrailLayer); layers.approachTrailLayer = null; }
        if (layers.approachTrailArrows) { map.removeLayer(layers.approachTrailArrows); layers.approachTrailArrows = null; }
    }

    // 1 ter. Zone de rendez-vous (rose), comme dans l'application.
    const zonePoints = formatPoints(session.meetingZonePoints);
    const zoneHash = pointsHash(zonePoints);
    if (zonePoints.length >= 3) {
        if (layers._lastZoneHash !== zoneHash) {
            layers._lastZoneHash = zoneHash;
            if (layers.meetingZoneLayer) map.removeLayer(layers.meetingZoneLayer);
            layers.meetingZoneLayer = L.polygon(zonePoints, { color: PASSAGE_COLORS.meeting, weight: 3, fillOpacity: 0.15, pane: 'corridorPane' }).addTo(map);
            if (session.meetingZoneName) layers.meetingZoneLayer.bindTooltip(session.meetingZoneName);
        }
    } else {
        layers._lastZoneHash = '';
        if (layers.meetingZoneLayer) { map.removeLayer(layers.meetingZoneLayer); layers.meetingZoneLayer = null; }
    }

    // 2. Corridor, couleur du passage — mis à jour uniquement si la zone, le mode ou l'état change
    if (session.isCorridorEnabled && !joiningTrail) {
        const corridorPoints = formatPoints(session.corridorZone);
        // Trous (intérieur d'une boucle) : anneau comme sur l'application, pas de disque plein.
        const corridorHoles = (Array.isArray(session.corridorHoles) ? session.corridorHoles : [])
            .map(formatPoints).filter(hole => hole.length >= 3);
        const corridorHash = pointsHash(corridorPoints) + corridorHoles.map(pointsHash).join('#') + routeColor;

        if (corridorPoints.length > 0) {
            if (layers._lastCorridorHash !== corridorHash) {
                layers._lastCorridorHash = corridorHash;
                if (layers.corridorLayer) map.removeLayer(layers.corridorLayer);
                layers.corridorLayer = L.polygon(corridorHoles.length ? [corridorPoints, ...corridorHoles] : corridorPoints, { color: routeColor, weight: 2, fillOpacity: 0.2, pane: 'corridorPane' }).addTo(map);
            }
        } else if (layers.corridorLayer) {
            layers._lastCorridorHash = '';
            map.removeLayer(layers.corridorLayer);
            layers.corridorLayer = null;
        }
    } else if (layers.corridorLayer) {
        layers._lastCorridorHash = '';
        map.removeLayer(layers.corridorLayer);
        layers.corridorLayer = null;
    }

    // 3. Position actuelle (Marker Victime, Bonhomme avec la main levée)
    if (session.currentLocation) {
        const lat = Number(session.currentLocation.latitude ?? session.currentLocation.lat);
        const lng = Number(session.currentLocation.longitude ?? session.currentLocation.lng);

        if (!isNaN(lat) && !isNaN(lng)) {
            const latLng = [lat, lng];
            // sessionType est l'état événementiel canonique utilisé aussi par la
            // timeline. Le type embarqué dans un ancien point GPS n'est qu'un
            // fallback de compatibilité.
            const effectiveType = session.sessionType || session.currentLocation.sessionType || 'FOLLOW';
            const victimColor = getSessionTypeColor(effectiveType);

            if (layers.currentPositionMarker) {
                layers.currentPositionMarker.setLatLng(latLng);
                if (layers.currentPositionMarker._lastColor !== victimColor) {
                    layers.currentPositionMarker._lastColor = victimColor;
                    const victimSvg = `<svg xmlns="http://www.w3.org/2000/svg" height="22" viewBox="0 0 24 24" width="22" fill="white">
                        <path d="M12 4m-2 0a2 2 0 1 1 4 0a2 2 0 1 1-4 0"/>
                        <path d="M15.89 8.11C15.5 7.72 14.83 7 13.53 7c-0.21 0-1.42 0-2.54 0C8.24 6.99 6 4.75 6 2H4c0 3.16 2.11 5.84 5 6.71V22h2v-6h2v6h2V10.05L18.95 14l1.41-1.41L15.89 8.11z"/>
                    </svg>`;
                    const victimIconHtml = `<div style="background-color: ${victimColor}; width: 36px; height: 36px; border-radius: 50%; border: 3px solid white; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">${victimSvg}</div>`;

                    layers.currentPositionMarker.setIcon(L.divIcon({
                        className: 'victim-person-icon',
                        html: victimIconHtml,
                        iconSize: [42, 42],
                        iconAnchor: [21, 21]
                    }));
                }
                layers.currentPositionMarker.setZIndexOffset(1000);
            } else {
                const victimSvg = `<svg xmlns="http://www.w3.org/2000/svg" height="22" viewBox="0 0 24 24" width="22" fill="white">
                    <path d="M12 4m-2 0a2 2 0 1 1 4 0a2 2 0 1 1-4 0"/>
                    <path d="M15.89 8.11C15.5 7.72 14.83 7 13.53 7c-0.21 0-1.42 0-2.54 0C8.24 6.99 6 4.75 6 2H4c0 3.16 2.11 5.84 5 6.71V22h2v-6h2v6h2V10.05L18.95 14l1.41-1.41L15.89 8.11z"/>
                </svg>`;
                const victimIconHtml = `<div style="background-color: ${victimColor}; width: 36px; height: 36px; border-radius: 50%; border: 3px solid white; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">${victimSvg}</div>`;

                layers.currentPositionMarker = L.marker(latLng, {
                    icon: L.divIcon({
                        className: 'victim-person-icon',
                        html: victimIconHtml,
                        iconSize: [42, 42],
                        iconAnchor: [21, 21]
                    }),
                    zIndexOffset: 1000
                }).addTo(map);
                layers.currentPositionMarker._lastColor = victimColor;
            }
        }
    } else if (layers.currentPositionMarker) {
        map.removeLayer(layers.currentPositionMarker);
        layers.currentPositionMarker = null;
    }

    // 4. Secoureurs
    if (session.responders) {
        const respondersArray = Object.values(session.responders);
        updateResponderMarkers(respondersArray, map, responderMarkersRef);
        respondersArray.forEach(responder => {
            drawResponderRoute(responder, map, layers);
        });
        cleanupOldResponders(respondersArray, map, layers, responderMarkersRef);
    } else {
        cleanupOldResponders([], map, layers, responderMarkersRef);
    }
}

/**
 * Nettoie les anciens secoureurs (markers et traces)
 */
const cleanupOldResponders = (respondersArray, map, layers, responderMarkersRef) => {
    const activeIds = new Set(respondersArray.map(r => String(r.userId)));

    if (responderMarkersRef && responderMarkersRef.current) {
        Object.keys(responderMarkersRef.current).forEach(id => {
            if (!activeIds.has(String(id))) {
                map.removeLayer(responderMarkersRef.current[id]);
                delete responderMarkersRef.current[id];
            }
        });
    }

    if (layers && layers.responderRouteArrows) {
        Object.keys(layers.responderRouteArrows).forEach(id => {
            if (!activeIds.has(String(id)) && layers.responderRouteArrows[id]) {
                map.removeLayer(layers.responderRouteArrows[id]);
                delete layers.responderRouteArrows[id];
            }
        });
    }
    if (layers && layers.responderRoutes) {
        Object.keys(layers.responderRoutes).forEach(id => {
            if (!activeIds.has(String(id))) {
                map.removeLayer(layers.responderRoutes[id]);
                delete layers.responderRoutes[id];
            }
        });
    }
};

/**
 * Dessine le tracé historique d'un secoureur spécifique
 */
const drawResponderRoute = (responder, map, layers) => {
    if (!map || !responder.route || responder.route.length === 0) return;

    const latLngs = responder.route.map(pt => [pt.latitude ?? pt.lat, pt.longitude ?? pt.lng]);
    if (!layers.responderRoutes) layers.responderRoutes = {};

    if (!layers.responderRouteArrows) layers.responderRouteArrows = {};
    drawArrows(map, layers.responderRouteArrows, responder.userId, latLngs, '#C8E6C9');
    if (layers.responderRoutes[responder.userId]) {
        layers.responderRoutes[responder.userId].setLatLngs(latLngs);
    } else {
        layers.responderRoutes[responder.userId] = L.polyline(latLngs, {
            color: "#4CAF50",
            weight: 4,
            opacity: 0.7,
            dashArray: '5, 5',
            pane: 'responderRoutePane'
        }).addTo(map);
    }
};

/**
 * Met à jour les marqueurs de secoureurs
 */
const updateResponderMarkers = (respondersArray, map, responderMarkersRef) => {
    if (!map || !responderMarkersRef) return;
    if (!responderMarkersRef.current) responderMarkersRef.current = {};

    respondersArray.forEach(resp => {
        const id = String(resp.userId);
        const lat = resp.lat ?? resp.latitude;
        const lng = resp.lng ?? resp.longitude;

        if (Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))) {
            const latLng = [lat, lng];
            const state = resp.state || null;
            const existing = responderMarkersRef.current[id];
            // Changement d'état (participant → alerte, intervenant…) : nouveau marqueur.
            if (existing && existing._sharedState !== state) {
                map.removeLayer(existing);
                delete responderMarkersRef.current[id];
            }
            if (responderMarkersRef.current[id]) {
                responderMarkersRef.current[id].setLatLng(latLng);
            } else if (state) {
                const color = RESPONDER_COLORS[state] || RESPONDER_COLORS.participant;
                const initial = String(resp.displayName || resp.userEmail || '?').trim().charAt(0).toUpperCase();
                const marker = L.marker(latLng, {
                    icon: L.divIcon({
                        className: 'shared-member-icon',
                        html: `<div style="background-color: ${color}; width: 34px; height: 34px; border-radius: 50%; border: 3px solid white; display: flex; align-items: center; justify-content: center; color: white; font-weight: 800; font-size: 15px; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">${state === 'alert' ? '⚠' : initial}</div>`,
                        iconSize: [40, 40],
                        iconAnchor: [20, 20],
                    }),
                    zIndexOffset: state === 'alert' ? 1000 : 900,
                }).addTo(map);
                marker._sharedState = state;
                marker.bindPopup(`<b>${RESPONDER_LABELS[state] || 'Participant'}</b><br/>${resp.displayName || resp.userEmail || ''}`);
                responderMarkersRef.current[id] = marker;
            } else {
                const helperSvg = `<svg xmlns="http://www.w3.org/2000/svg" height="22" viewBox="0 0 24 24" width="22" fill="white">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 14h-2v-2h2v2zm0-4h-2V7h2v5z"/>
                </svg>`;
                const helperIcon = L.divIcon({
                    className: 'responder-marker-icon',
                    html: `<div style="background-color: #4CAF50; width: 36px; height: 36px; border-radius: 50%; border: 3px solid white; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">${helperSvg}</div>`,
                    iconSize: [42, 42],
                    iconAnchor: [21, 21]
                });

                const marker = L.marker(latLng, { icon: helperIcon, zIndexOffset: 900 }).addTo(map);
                marker._sharedState = null;
                if (resp.userEmail) {
                    marker.bindPopup(`<b>Secoureur</b><br/>${resp.userEmail}`);
                }
                responderMarkersRef.current[id] = marker;
            }
        }
    });
};
